import { useState } from 'react';

import chevronLeftIcon from '@shared/assets/images/chevron-left_icon.svg';
import chevronRightIcon from '@shared/assets/images/chevron-right_black_icon.svg';

import styles from './CommentSection.module.scss';

/**
 * CommentSection 컴포넌트
 * - 코멘트 섹션 컴포넌트 (Figma: container : referencedocs와 유사한 구조)
 * - 섹션 타이틀 + 테이블 (작성자, 코멘트) 구조
 * - 코멘트 목록 표시 (읽기 전용)
 * - 2개 이상일 경우 페이지네이션 버튼 표시
 *
 * @param {Array} comments - 코멘트 목록
 * @param {boolean} isPreview - 미리보기 모드 여부
 * @param {boolean} showWriteButton - 작성 버튼 표시 여부
 * @param {Function} onWriteClick - 작성 버튼 클릭 핸들러
 * @param {Function} onCommentClick - 코멘트 클릭 핸들러
 */
function CommentSection({
  comments = [],
  isPreview = false,
  showWriteButton = false,
  onWriteClick,
  onCommentClick,
}) {
  const [currentIndex, setCurrentIndex] = useState(0);

  // 현재 표시할 코멘트
  const currentComment = comments.length > 0 ? comments[currentIndex] : null;
  const writerName = currentComment?.enumber_name || currentComment?.name || '-';
  const commentText = currentComment?.comment || '-';
  const totalCount = comments.length;
  const hasMultipleComments = totalCount >= 2;

  // 이전 코멘트로 이동
  function handlePrev() {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }

  // 다음 코멘트로 이동
  function handleNext() {
    if (currentIndex < totalCount - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  }

  // 이전 버튼 비활성화 여부
  const isPrevDisabled = currentIndex === 0;
  // 다음 버튼 비활성화 여부
  const isNextDisabled = currentIndex >= totalCount - 1;

  // 코멘트가 없으면 빈 상태 표시
  if (comments.length === 0) {
    return (
      <div className={styles.commentSection}>
        <div className={styles.sectionHeaderContainer}>
          <h3 className={styles.sectionTitle}>코멘트</h3>
          {showWriteButton && (
            <div className={styles.headerControls}>
              <button type="button" className={styles.writeButton} onClick={onWriteClick}>
                작성
              </button>
            </div>
          )}
        </div>
        <div className={styles.tableContainer}>
          <div className={styles.tableTopLine} />
          {/* 작성자와 코멘트를 한 줄에 배치 - 피그마: flex h-[26px] */}
          <div className={styles.commentRow}>
            {/* 작성자 필드 - 피그마: basis-0 flex gap-[10px] grow */}
            <div className={styles.commentField}>
              <label className={styles.label}>작성자</label>
              <span className={styles.commentValue}>-</span>
            </div>
            {/* 코멘트 필드 - 피그마: basis-0 flex gap-[10px] grow */}
            <div className={styles.commentField}>
              <label className={styles.label}>코멘트</label>
              <span className={styles.commentValue}>-</span>
            </div>
          </div>
          <div className={styles.tableBottomLine} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.commentSection}>
      {/* 섹션 헤더 - 타이틀 + 작성 버튼 + 페이지네이션 버튼 */}
      <div className={styles.sectionHeaderContainer}>
        <h3 className={styles.sectionTitle}>코멘트</h3>
        <div className={styles.headerControls}>
          {showWriteButton && (
            <button type="button" className={styles.writeButton} onClick={onWriteClick}>
              작성
            </button>
          )}
          {hasMultipleComments && (
            <div className={styles.paginationControls}>
              <button
                type="button"
                className={`${styles.paginationButton} ${isPrevDisabled ? styles.disabled : ''}`}
                onClick={handlePrev}
                disabled={isPrevDisabled}
                aria-label="이전 코멘트"
              >
                <img src={chevronLeftIcon} alt="" className={styles.paginationIcon} />
              </button>
              <span className={styles.paginationText}>
                {currentIndex + 1}/{totalCount}
              </span>
              <button
                type="button"
                className={`${styles.paginationButton} ${isNextDisabled ? styles.disabled : ''}`}
                onClick={handleNext}
                disabled={isNextDisabled}
                aria-label="다음 코멘트"
              >
                <img src={chevronRightIcon} alt="" className={styles.paginationIcon} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 테이블 컨테이너 - Figma: Frame 427321522 */}
      <div className={styles.tableContainer}>
        {/* 상단 파란색 라인 */}
        <div className={styles.tableTopLine} />

        {/* 작성자와 코멘트를 한 줄에 배치 - 피그마: flex h-[26px] */}
        <div
          className={`${styles.commentRow} ${onCommentClick ? styles.clickable : ''}`}
          onClick={function () {
            if (onCommentClick && currentComment) {
              onCommentClick(currentComment);
            }
          }}
          role={onCommentClick ? 'button' : undefined}
          tabIndex={onCommentClick ? 0 : undefined}
          onKeyDown={function (e) {
            if (onCommentClick && currentComment && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              onCommentClick(currentComment);
            }
          }}
        >
          {/* 작성자 필드 - 피그마: basis-0 flex gap-[10px] grow */}
          <div className={styles.commentField}>
            <label className={styles.label}>작성자</label>
            <span className={styles.commentValue}>{writerName}</span>
          </div>
          {/* 코멘트 필드 - 피그마: basis-0 flex gap-[10px] grow */}
          <div className={styles.commentField}>
            <label className={styles.label}>코멘트</label>
            <span className={styles.commentValue}>{commentText}</span>
          </div>
        </div>

        {/* 하단 구분선 */}
        <div className={styles.tableBottomLine} />
      </div>
    </div>
  );
}

export default CommentSection;
